/**
 * Responsibility: Implement inventory check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"
import { countInvalidInventoryItems, findDuplicateNames } from './common'

export const inventoryCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []

  const playerInventory = state.player.inventory ?? []
  const invalidPlayerItems = countInvalidInventoryItems(playerInventory)
  if (invalidPlayerItems > 0) {
    incidents.push({
      code: 'inventory_player_items_invalid',
      domain: 'inventory',
      severity: 'high',
      confidence: 0.98,
      message: `${invalidPlayerItems} player inventory entries are malformed.`,
      remediationPlan: 'Normalize player inventory entries and drop blank item names.',
      context: { invalidPlayerItems },
    })
  }

  const duplicateNames = findDuplicateNames(playerInventory.map(item => String(item?.name ?? '')))
  if (duplicateNames.length > 0) {
    incidents.push({
      code: 'inventory_player_duplicate_names',
      domain: 'inventory',
      severity: 'medium',
      confidence: 0.96,
      message: `Duplicate player inventory items detected (${duplicateNames.length}).`,
      remediationPlan: 'Deduplicate inventory by case-insensitive item name.',
      context: { duplicates: duplicateNames.slice(0, 12) },
    })
  }

  let invalidNpcInventory = 0
  for (const npc of state.currentRoom.npcs ?? []) {
    if (!Array.isArray(npc?.inventory)) continue
    for (const item of npc.inventory) {
      if (!String(item ?? '').trim()) invalidNpcInventory += 1
    }
  }
  for (const npc of state.world.npcs ?? []) {
    if (!Array.isArray(npc?.inventory)) continue
    for (const item of npc.inventory) {
      if (!String(item ?? '').trim()) invalidNpcInventory += 1
    }
  }

  if (invalidNpcInventory > 0) {
    incidents.push({
      code: 'inventory_npc_items_invalid',
      domain: 'inventory',
      severity: 'low',
      confidence: 0.95,
      message: `${invalidNpcInventory} NPC inventory entries are empty/invalid.`,
      remediationPlan: 'Trim and remove empty NPC inventory names.',
      context: { invalidNpcInventory },
    })
  }

  return { passed: incidents.length === 0, incidents }
}
